import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { exportFacilitiesAPI } from "../../api/api";
import { handleExportResponse } from "../../utils/export";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import { useStandardizedTracking } from "../../utils/pendo/useStandardizedTracking";
import { FACILITY_EVENTS, FACILITY_FEATURE_GROUP, FACILITY_PAGE_KEY } from "./analytics";

// Physician-export parity: the spinner stays visible for at least this long so the click feels acknowledged.
const MIN_SPINNER_MS = 500;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// UI column-selector key → backend export column key(s). Keys the backend always includes
// (id, name, npi, createdAt/By, updatedAt/By) are not listed here.
const UI_TO_EXPORT_COLUMN_KEYS = Object.freeze({
  taxId: ["taxId"],
  address: ["address"],
  suiteNumber: ["suiteNumber"],
  city: ["city"],
  state: ["state"],
  zipCode: ["zipCode"],
  phone: ["phone"],
  matchingString: ["providerMatchingAliases"]
});

// The exported file mirrors the table's column selection: hidden optional
// columns are dropped; id/name/npi and the audit columns are always exported.
export const hiddenColumnsToExcludedExportKeys = (hiddenColumns = []) =>
  hiddenColumns.flatMap((uiKey) => UI_TO_EXPORT_COLUMN_KEYS[uiKey] || []);

/**
 * Export the facilities currently shown in Settings › Facilities (MVP-31132).
 * Sends the visible ids (already filtered + sorted) to POST /facilities/export and downloads the
 * returned CSV/XLSX via the shared handleExportResponse. Fires the PRD export events.
 */
export const useFacilityExport = () => {
  const { t } = useTranslation();
  const { track } = useStandardizedTracking();
  const [isExporting, setIsExporting] = useState(false);

  const trackExport = useCallback(
    (eventName, additionalInfo) =>
      track(eventName, {
        featureGroup: FACILITY_FEATURE_GROUP,
        pageKey: FACILITY_PAGE_KEY,
        contentKey: "export",
        additionalInfo
      }),
    [track]
  );

  const exportFacilities = useCallback(
    async (facilities, fileType, hiddenColumns = []) => {
      const facilityIds = facilities.map((facility) => facility.id);
      const excludedColumns = hiddenColumnsToExcludedExportKeys(hiddenColumns);
      const startedAt = Date.now();
      setIsExporting(true);
      trackExport(FACILITY_EVENTS.EXPORT_CLICKED, { facility_count_exported: facilityIds.length, format: fileType });
      try {
        const response = await exportFacilitiesAPI({ facilityIds, fileType, excludedColumns });
        handleExportResponse(response);
        SuccessMessage(t("facility_management.list.export_success"));
        trackExport(FACILITY_EVENTS.EXPORT_SUCCEEDED, {
          facility_count_exported: facilityIds.length,
          format: fileType
        });
        return { success: true };
      } catch (error) {
        console.error("Error exporting facilities:", error);
        ErrorMessage(t("facility_management.list.export_error"));
        trackExport(FACILITY_EVENTS.EXPORT_FAILED, {
          facility_count_exported: facilityIds.length,
          format: fileType,
          error_type: error?.response?.status ? `http_${error.response.status}` : "network"
        });
        return { success: false, error };
      } finally {
        await wait(Math.max(MIN_SPINNER_MS - (Date.now() - startedAt), 0));
        setIsExporting(false);
      }
    },
    [t, trackExport]
  );

  return { exportFacilities, isExporting };
};

export default useFacilityExport;
